/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * VPS Knowledge Mining Service
 * 
 * API client for VPS Knowledge Mining endpoints.
 * Uses shared response types and handleApiResponse for consistent error handling.
 * 
 * For simple GET endpoints, components can use createQueryFn directly:
 *   import { createQueryFn } from '@shared/lib/api';
 *   const { data } = useQuery({
 *     queryKey: KNOWLEDGE_MINING_QUERY_KEYS.sources,
 *     queryFn: createQueryFn<ListKnowledgeSourcesResponseData>(KNOWLEDGE_MINING_ENDPOINTS.sources),
 *   });
 */

import { authenticatedFetch, createQueryFn, createMutationFn } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import type {
  KnowledgeSource,
  ListKnowledgeSourcesResponseData,
  ListMiningJobsResponseData,
  MiningJobResponseData,
  ListMiningRunsResponseData,
  MiningRunListItem,
  ClassifyDocumentResponseData,
  FindSimilarPlaybooksResponseData,
  GetStrategiesResponseData,
  SyncToRemoteResponseData,
  GenerateSinglePlaybookResponseData,
} from '@shared/types/responses/knowledge-mining';
import type {
  CreateKnowledgeSourceInput,
  CreateFilesystemSourceInput,
  TestSourceConnectionRequest,
} from '@shared/types/requests/knowledge-mining';
import type {
  ChunkingStrategyType,
  GenerationStrategyType,
  KnowledgeSourceType,
  KnowledgeSourceConfigInput,
  FilesystemConfig,
} from '@shared/schema/knowledgeMining';

// ============================================================================
// Product Server Endpoints (for path resolution)
// ============================================================================

const PRODUCT_SERVER_BASE = import.meta.env.DEV ? 'http://localhost:3002' : '';

/**
 * Resolve a relative path to an absolute path using the product server.
 * 
 * For filesystem knowledge sources, paths can be entered as:
 * - Absolute paths (e.g., '/Users/name/docs') - returned as-is
 * - Relative paths (e.g., 'docs') - resolved relative to the product repo root
 * 
 * The knowledge-mining API requires absolute paths, so this function should be
 * called to resolve relative paths before creating or testing a source.
 * 
 * @param relativePath - The path to resolve (can be relative or absolute)
 * @param repoUrl - Repository URL (required for relative paths)
 * @param repoPath - Monorepo path (optional, for monorepos)
 * @returns The absolute path
 * @throws Error if resolution fails
 */
export async function resolveFilesystemPath(
  relativePath: string,
  repoUrl?: string,
  repoPath?: string
): Promise<{ absolutePath: string; wasResolved: boolean }> {
  // If already absolute, return as-is (no API call needed)
  if (relativePath.startsWith('/') || /^[A-Za-z]:[\\/]/.test(relativePath)) {
    return { absolutePath: relativePath, wasResolved: false };
  }
  
  // Call the product server to resolve the path
  const response = await fetch(`${PRODUCT_SERVER_BASE}/api/resolve-path`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      repoUrl,
      repoPath,
      relativePath,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to resolve path: ${response.status}`);
  }
  
  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to resolve path');
  }
  
  return {
    absolutePath: result.data.absolutePath,
    wasResolved: result.data.wasResolved,
  };
}

/**
 * Directory item returned by the browse-directory endpoint
 */
export interface DirectoryItem {
  /** Name of the file or directory */
  name: string;
  /** Whether this is a directory (true) or file (false) */
  isDirectory: boolean;
  /** Relative path from the repo root */
  path: string;
  /** Absolute path on the filesystem */
  absolutePath: string;
  /** File size in bytes (only for files) */
  size?: number;
}

/**
 * Response from the browse-directory endpoint
 */
export interface BrowseDirectoryResult {
  /** Items in the directory (files and subdirectories) */
  items: DirectoryItem[];
  /** Current path relative to repo root */
  currentPath: string;
  /** Absolute path of the current directory */
  absolutePath: string;
  /** Parent path relative to repo root (null if at repo root) */
  parentPath: string | null;
  /** Base path of the repository */
  repoBasePath?: string;
  /** Whether navigation up is possible */
  canGoUp: boolean;
}

/**
 * Browse directory contents for filesystem source selection.
 * 
 * This provides a directory browser for users to navigate and select
 * directories as knowledge sources.
 * 
 * @param currentPath - The path to browse (relative or absolute). If empty, shows repo root.
 * @param repoUrl - Repository URL (required for relative paths)
 * @param repoPath - Monorepo path (optional, for monorepos)
 * @returns Directory contents including files and subdirectories
 * @throws Error if browsing fails
 */
export async function browseDirectory(
  currentPath: string,
  repoUrl?: string,
  repoPath?: string
): Promise<BrowseDirectoryResult> {
  const response = await fetch(`${PRODUCT_SERVER_BASE}/api/browse-directory`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      repoUrl,
      repoPath,
      currentPath,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to browse directory: ${response.status}`);
  }
  
  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to browse directory');
  }
  
  return result.data;
}

// ============================================================================
// API Endpoints - Centralized endpoint definitions
// ============================================================================

export const KNOWLEDGE_MINING_ENDPOINTS = {
  sources: '/api/knowledge-mining/sources',
  sourceDetail: (id: string) => `/api/knowledge-mining/sources/${id}`,
  testConnection: '/api/knowledge-mining/sources/test-connection',
  jobs: '/api/knowledge-mining/jobs',
  jobDetail: (id: string) => `/api/knowledge-mining/jobs/${id}`,
  jobStatus: (id: string) => `/api/knowledge-mining/jobs/${id}/status`,
  jobCancel: (id: string) => `/api/knowledge-mining/jobs/${id}/cancel`,
  runs: '/api/knowledge-mining/runs',
  classify: '/api/knowledge-mining/classify',
  similarPlaybooks: '/api/knowledge-mining/similar-playbooks',
  strategies: '/api/knowledge-mining/strategies',
  sync: '/api/knowledge-mining/sync',
  generatePlaybook: '/api/knowledge-mining/generate-playbook',
} as const;

// ============================================================================
// Query Keys - For React Query cache management
// ============================================================================

export const KNOWLEDGE_MINING_QUERY_KEYS = {
  all: ['knowledge-mining'] as const,
  sources: ['knowledge-mining', 'sources'] as const,
  jobs: ['knowledge-mining', 'jobs'] as const,
  jobDetail: (id: string) => ['knowledge-mining', 'jobs', id] as const,
  runs: ['knowledge-mining', 'runs'] as const,
  strategies: ['knowledge-mining', 'strategies'] as const,
} as const;

// Re-export types from shared - these are the canonical type definitions
export type { KnowledgeSource } from '@shared/types/responses/knowledge-mining';
export type { 
  CreateKnowledgeSourceInput, 
  CreateFilesystemSourceInput,
  CreateConfluenceSourceInput,
  CreateGcsSourceInput,
  CreateSharePointSourceInput,
} from '@shared/types/requests/knowledge-mining';

// Type aliases for convenience
export type SourceType = KnowledgeSourceType;
export type ChunkingStrategy = ChunkingStrategyType;
export type GenerationStrategy = GenerationStrategyType;

export type { MiningRunListItem as MiningRun };
export type { MiningJobResponseData };

// MiningJob extends the API response with convenience alias
export interface MiningJob extends MiningJobResponseData {
  /** Alias for jobId for convenience */
  id: string;
}

// ============================================================================
// Query/Mutation Function Factories - For direct use in components
// ============================================================================

/** Query function for knowledge sources - use with useQuery */
export const knowledgeSourcesQueryFn = createQueryFn<ListKnowledgeSourcesResponseData>(KNOWLEDGE_MINING_ENDPOINTS.sources);

/** Query function for mining strategies - use with useQuery */
export const miningStrategiesQueryFn = createQueryFn<GetStrategiesResponseData>(KNOWLEDGE_MINING_ENDPOINTS.strategies);

/** Query function for mining runs - use with useQuery */
export const miningRunsQueryFn = createQueryFn<ListMiningRunsResponseData>(KNOWLEDGE_MINING_ENDPOINTS.runs);

// ============================================================================
// Service Functions - For complex operations with transformations
// ============================================================================

/**
 * List knowledge sources
 */
export async function listKnowledgeSources(): Promise<KnowledgeSource[]> {
  const response = await authenticatedFetch('/api/knowledge-mining/sources');
  const data = await handleApiResponse<ListKnowledgeSourcesResponseData>(response);
  return data.sources;
}

/**
 * Create a new knowledge source
 * 
 * Constructs the full request body expected by the API using discriminated union:
 * - Generates a unique ID
 * - Sets enabled to true by default
 * - Formats auth/config based on source type:
 *   - filesystem: auth={}, config={basePath, ...}
 *   - confluence: auth={baseUrl, username, apiToken}, config={spaceKey, ...}
 *   - gcs: auth={projectId, ...}, config={bucketName, ...}
 * - Sets targetScope to 'organizational' by default
 * 
 * IMPORTANT: For filesystem sources, config.basePath must be an absolute path.
 * Use resolveFilesystemPath() to convert relative paths before calling this function.
 * 
 * @param source - Typed source creation input (discriminated by type)
 */
export async function createKnowledgeSource(
  source: CreateKnowledgeSourceInput
): Promise<KnowledgeSource> {
  // Generate a unique ID based on name and timestamp
  const id = `${source.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now().toString(36)}`;
  
  // Build the full request body conforming to KnowledgeSourceConfigInput
  // The discriminated union ensures type safety for auth/config based on source.type
  const requestBody: KnowledgeSourceConfigInput = {
    id,
    name: source.name,
    type: source.type,
    enabled: true,
    auth: source.auth ?? {},
    config: source.config,
    targetScope: source.targetScope ?? 'organizational',
  } as KnowledgeSourceConfigInput;
  
  const response = await authenticatedFetch('/api/knowledge-mining/sources', {
    method: 'POST',
    body: requestBody,
  });
  const data = await handleApiResponse<{ source: KnowledgeSource }>(response);
  return data.source;
}

/** Response type for validateSourceConfig */
export interface ValidateSourceConfigResult {
  success: boolean;
  message: string;
  details?: {
    filesystemInfo?: {
      basePath: string;
      fileCount: number;
      totalSize: number;
      extensions: string[];
    };
    confluenceSpaces?: { key: string; name: string }[];
    gcsBuckets?: { name: string; location: string }[];
    sharepointSite?: {
      siteName?: string;
      siteUrl?: string;
      documentLibraries?: number;
    };
  };
}

/** Input parameters for validateSourceConfig based on source type */
export type ValidateSourceConfigParams = 
  | { type: 'filesystem'; basePath: string }
  | { type: 'confluence'; baseUrl: string; username: string; apiToken: string }
  | { type: 'gcs'; projectId: string; keyFileContent?: string; keyFilePath?: string }
  | { type: 'sharepoint'; tenantId: string; clientId: string; clientSecret: string; siteUrl: string };

/**
 * Test connection to a knowledge source configuration BEFORE creation
 * 
 * Validates source configuration without creating the source.
 * Uses discriminated union based on source type:
 * - Filesystem: validates that basePath exists and is accessible
 * - Confluence: validates authentication credentials
 * - GCS: validates project and bucket access
 * 
 * IMPORTANT: For filesystem sources, basePath MUST be an absolute path.
 * Use `resolveFilesystemPath()` first to convert relative paths to absolute.
 * 
 * @param params - Type-discriminated configuration object
 */
export async function validateSourceConfig(
  params: ValidateSourceConfigParams
): Promise<ValidateSourceConfigResult> {
  // Build the discriminated request body based on type
  let requestBody: TestSourceConnectionRequest;
  
  if (params.type === 'filesystem') {
    // Filesystem uses config.basePath, not auth
    requestBody = {
      type: 'filesystem',
      config: { basePath: params.basePath },
    };
  } else if (params.type === 'confluence') {
    requestBody = {
      type: 'confluence',
      auth: {
        baseUrl: params.baseUrl,
        username: params.username,
        apiToken: params.apiToken,
      },
    };
  } else if (params.type === 'gcs') {
    requestBody = {
      type: 'gcs',
      auth: {
        projectId: params.projectId,
        keyFileContent: params.keyFileContent,
        keyFilePath: params.keyFilePath,
      },
    };
  } else if (params.type === 'sharepoint') {
    requestBody = {
      type: 'sharepoint',
      auth: {
        tenantId: params.tenantId,
        clientId: params.clientId,
        clientSecret: params.clientSecret,
      },
      config: {
        siteUrl: params.siteUrl,
      },
    };
  } else {
    // TypeScript exhaustiveness check - this should never happen
    const _exhaustive: never = params;
    throw new Error(`Unknown source type: ${(_exhaustive as any).type}`);
  }
  
  const response = await authenticatedFetch('/api/knowledge-mining/sources/test-connection', {
    method: 'POST',
    body: requestBody,
  });
  return handleApiResponse(response);
}

/**
 * Test connection to an existing knowledge source by ID
 * @deprecated Use validateSourceConfig for pre-creation validation
 */
export async function testSourceConnection(
  sourceId: string
): Promise<{ success: boolean; message: string }> {
  // Get the source first to extract its type and config
  const response = await authenticatedFetch(`/api/knowledge-mining/sources/${sourceId}`);
  const source = await handleApiResponse<KnowledgeSource>(response);
  
  // Now test using the source's configuration
  if (source.type === 'filesystem') {
    // Cast config since schema uses union types (not discriminated)
    const config = source.config as FilesystemConfig;
    if (!config.basePath) {
      throw new Error('No filesystem path configured for this source');
    }
    return validateSourceConfig({ type: 'filesystem', basePath: config.basePath });
  }
  
  // TODO: Handle confluence and gcs
  throw new Error(`Testing ${source.type} sources is not yet implemented`);
}

/**
 * Delete a knowledge source
 */
export async function deleteKnowledgeSource(sourceId: string): Promise<void> {
  const response = await authenticatedFetch(`/api/knowledge-mining/sources/${sourceId}`, {
    method: 'DELETE',
  });
  await handleApiResponse<void>(response);
}

/**
 * List mining jobs
 */
export async function listMiningJobs(options?: {
  status?: string;
  limit?: number;
}): Promise<MiningJob[]> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.limit) params.set('limit', options.limit.toString());

  const url = `/api/knowledge-mining/jobs${params.toString() ? `?${params}` : ''}`;
  const response = await authenticatedFetch(url);
  const data = await handleApiResponse<ListMiningJobsResponseData>(response);
  // Add id alias for convenience
  return data.jobs.map(job => ({ ...job, id: job.jobId }));
}

/**
 * Create a new mining job
 */
export async function createMiningJob(params: {
  sourceId: string;
  /** Mining run scope - 'product' stores in product repo, 'organization' stores in velocity repo */
  scope?: 'product' | 'organization';
  /** Product ID for product-scoped runs */
  productId?: string;
  /** Product repo path for product-scoped runs */
  productRepoPath?: string;
  chunkingStrategy?: ChunkingStrategy;
  generationStrategy?: GenerationStrategy;
  useAiClassification?: boolean;
  /** Whether to generate playbook drafts (defaults to true) */
  generateDrafts?: boolean;
}): Promise<MiningJob> {
  const {
    sourceId,
    scope,
    productId,
    productRepoPath,
    chunkingStrategy,
    generationStrategy,
    useAiClassification,
    generateDrafts = true, // Default to true - this is the primary purpose of mining jobs
  } = params;
  
  // Build the request body matching the API schema
  const requestBody = {
    sourceId,
    scope,
    productId,
    productRepoPath,
    options: {
      generateDrafts,
      strategyConfig: {
        chunking: chunkingStrategy ? { strategy: chunkingStrategy } : undefined,
        generation: generationStrategy ? { strategy: generationStrategy } : undefined,
      },
      classificationOptions: useAiClassification !== undefined ? { useAi: useAiClassification } : undefined,
    },
  };
  
  const response = await authenticatedFetch('/api/knowledge-mining/jobs', {
    method: 'POST',
    body: requestBody,
  });
  const job = await handleApiResponse<MiningJobResponseData>(response);
  return { ...job, id: job.jobId };
}

/**
 * Get a specific mining job
 */
export async function getMiningJob(jobId: string): Promise<MiningJob> {
  const response = await authenticatedFetch(`/api/knowledge-mining/jobs/${jobId}`);
  const job = await handleApiResponse<MiningJobResponseData>(response);
  return { ...job, id: job.jobId };
}

/**
 * Get mining job status (for polling)
 */
export async function getMiningJobStatus(jobId: string): Promise<{
  jobId: string;
  status: string;
  progress: { phase: string; percentage: number; };
  error?: { code: string; message: string; };
}> {
  const response = await authenticatedFetch(`/api/knowledge-mining/jobs/${jobId}/status`);
  return handleApiResponse(response);
}

/**
 * Cancel a mining job
 */
export async function cancelMiningJob(jobId: string): Promise<void> {
  const response = await authenticatedFetch(`/api/knowledge-mining/jobs/${jobId}/cancel`, {
    method: 'POST',
  });
  await handleApiResponse<void>(response);
}

/**
 * List mining runs (job history with playbook counts)
 * 
 * @param options.productId - Optional product ID to include product-scoped runs
 * @param options.productRepoPath - Optional direct repo path (alternative to productId)
 */
export async function listMiningRuns(options?: {
  productId?: string;
  productRepoPath?: string;
}): Promise<MiningRunListItem[]> {
  const params = new URLSearchParams();
  if (options?.productId) params.set('productId', options.productId);
  if (options?.productRepoPath) params.set('productRepoPath', options.productRepoPath);
  
  const url = `/api/knowledge-mining/runs${params.toString() ? `?${params}` : ''}`;
  const response = await authenticatedFetch(url);
  const data = await handleApiResponse<ListMiningRunsResponseData>(response);
  return data.runs;
}

/**
 * Classify a document
 */
export async function classifyDocument(
  title: string,
  content: string,
  options?: {
    useAi?: boolean;
  }
): Promise<ClassifyDocumentResponseData> {
  const response = await authenticatedFetch('/api/knowledge-mining/classify', {
    method: 'POST',
    body: { 
      document: { title, textContent: content }, 
      options: { useAi: options?.useAi },
    },
  });
  return handleApiResponse<ClassifyDocumentResponseData>(response);
}

/**
 * Find similar existing playbooks
 */
export async function findSimilarPlaybooks(
  title: string,
  content: string,
  options?: {
    limit?: number;
    minSimilarity?: number;
  }
): Promise<FindSimilarPlaybooksResponseData> {
  const response = await authenticatedFetch('/api/knowledge-mining/similar-playbooks', {
    method: 'POST',
    body: {
      title,
      content,
      limit: options?.limit ?? 5,
      minSimilarity: options?.minSimilarity ?? 0.5,
    },
  });
  return handleApiResponse<FindSimilarPlaybooksResponseData>(response);
}

/**
 * Get available mining strategies
 */
export async function getMiningStrategies(): Promise<GetStrategiesResponseData> {
  const response = await authenticatedFetch('/api/knowledge-mining/strategies');
  return handleApiResponse<GetStrategiesResponseData>(response);
}

/**
 * Sync changes to remote repository
 */
export async function syncToRemote(options?: {
  commitMessage?: string;
}): Promise<SyncToRemoteResponseData> {
  const response = await authenticatedFetch('/api/knowledge-mining/sync', {
    method: 'POST',
    body: options,
  });
  return handleApiResponse<SyncToRemoteResponseData>(response);
}

// ============================================================================
// Single Playbook Generation
// ============================================================================

/**
 * Generate a single playbook from a knowledge source for a specific topic
 * 
 * This function:
 * 1. Creates a temporary document index for the source
 * 2. Uses an agent with source_query tool to research the topic
 * 3. Classifies and writes the generated playbook
 * 
 * @param options.sourceId - ID of the knowledge source to use
 * @param options.topic - Topic for the playbook (e.g., "security standards for PII")
 * @param options.category - Optional category override ('product' | 'organizational')
 * @param options.productRepoPath - Optional product repo path for product-scoped playbooks
 * @returns Generated playbook information including path and statistics
 */
export async function generateSinglePlaybook(options: {
  sourceId: string;
  topic: string;
  category?: 'product' | 'organizational';
  productRepoPath?: string;
}): Promise<GenerateSinglePlaybookResponseData> {
  const response = await authenticatedFetch(KNOWLEDGE_MINING_ENDPOINTS.generatePlaybook, {
    method: 'POST',
    body: options,
  });
  return handleApiResponse<GenerateSinglePlaybookResponseData>(response);
}

// Re-export response type for convenience
export type { GenerateSinglePlaybookResponseData };
